import React from "react";
import Img from "@/components/Img";

/**
 * PageHero — the canonical dark-hero pattern used across every non-home page.
 *
 * Matches the /wholesale hero exactly: full-bleed backdrop image behind a
 * ~60% black veil, a small burgundy eyebrow, a large display H1 (with an
 * optional burgundy-accented span), and a muted subhead.
 *
 * Usage:
 *   <PageHero
 *     image="/media/craft-poster.jpg"
 *     imageAlt="A Perth family opening their Butcher Box delivery"
 *     eyebrow="Curated Boxes"
 *     title={<>Seven boxes.<br/><span className="text-[color:var(--tbb-burgundy)]">One weekend of great meat.</span></>}
 *     subhead="Hand-picked by our butchers, packed in Malaga, delivered cold every Saturday."
 *     testId="boxes-hero"
 *   />
 *
 * The heading uses the existing `.hero-img` CSS class (defined in
 * `frontend/src/index.css`) so pre-existing global styling still applies.
 *
 * Sept 2026 — switched from a plain <img> to <Img> so hero images
 * pick up the responsive srcset pipeline. On /build-your-own this
 * drops mobile hero payload from 132 KB to 60 KB and is the LCP win
 * needed to bring that page back under 3s.
 */
export default function PageHero({
  image,
  imageAlt = "",
  eyebrow,
  title,
  subhead,
  minHeight = "60vh",
  align = "left", // "left" (default) or "center"
  children, // optional slot rendered at the bottom of the hero body (e.g., inline CTAs)
  testId,
}) {
  const alignCls = align === "center" ? "items-center text-center" : "";
  return (
    <section
      className="relative bg-black text-white overflow-hidden"
      style={{ minHeight }}
      data-testid={testId}
    >
      <Img
        src={image}
        alt={imageAlt}
        className="hero-img"
        priority
        sizes="100vw"
      />
      <div className="absolute inset-0 bg-black/60" />
      <div
        className={`relative tbb-container z-10 flex flex-col justify-center py-20 md:py-24 ${alignCls}`}
        style={{ minHeight }}
      >
        {eyebrow ? (
          <div className="overline text-white/70 mb-4">{eyebrow}</div>
        ) : null}
        {title ? (
          <h1
            className={`font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-none ${align === "center" ? "mx-auto" : "max-w-3xl"}`}
            data-testid={testId ? `${testId}-heading` : undefined}
          >
            {title}
          </h1>
        ) : null}
        {subhead ? (
          <p className={`mt-6 text-white/80 text-lg leading-relaxed ${align === "center" ? "mx-auto max-w-2xl" : "max-w-2xl"}`}>
            {subhead}
          </p>
        ) : null}
        {children ? <div className="mt-8">{children}</div> : null}
      </div>
    </section>
  );
}
